export type Vertical = "travel" | "education" | "healthcare";

export type SuggestArgs = {
  vertical: Vertical;
  amountAED: number;
  context?: Record<string, any>;
};

export type Plan = {
  id: string;
  label: string;
  breakdown: {
    bnplAED?: number;
    bankFinanceAED?: number;
    upfrontAED?: number;
  };
  aprPercent?: number;
  tenureMonths?: number;
  notes?: string[];
  redirectUrl?: string;
  provider?: 'tabby' | 'tamara' | 'postpay' | 'emi';
  isPrimary?: boolean;
};

/**
 * Flexaura Checkout Routing Thresholds
 * These define the moat - dynamic provider routing based on vertical and ticket size
 */
const THRESHOLDS = {
  travel: {
    tier1Max: 8000,    // Tabby, Tamara, Postpay
    tier2Max: 15000,   // Tabby primary, Tamara secondary, hide Postpay
    // > 15000: Tabby only + EMI fallback
  },
  healthcare: {
    tier1Max: 6000,    // Tabby, Postpay
    tier2Max: 15000,   // Tabby, Tamara
    // > 15000: Tabby + Bank EMI
  },
  education: {
    tier1Max: 6000,    // BNPL (Tabby, Tamara)
    tier2Max: 25000,   // Tabby + EMI side-by-side
    // > 25000: EMI first, BNPL as partial-pay
  },
};

const BNPL_MIN = 2000;

const BNPL_PROVIDERS = [
  { id: "tabby", name: "Tabby", redirect: "/partners/tabby" },
  { id: "tamara", name: "Tamara", redirect: "/partners/tamara" },
  { id: "postpay", name: "Postpay", redirect: "/partners/postpay" },
];

const BANK_LENDERS = [
  { id: "emi-bank", name: "Bank EMI", redirect: "/partners/bank-emi" },
  { id: "edu-finance", name: "Education Finance", redirect: "/partners/edu-finance" },
];

export function suggestFinancePlans(args: SuggestArgs): Plan[] {
  const { vertical, amountAED, context = {} } = args;
  const plans: Plan[] = [];

  // If amount is tiny, encourage upfront
  if (amountAED < BNPL_MIN) {
    plans.push({
      id: "upfront",
      label: "Pay upfront",
      breakdown: { upfrontAED: amountAED },
      notes: ["Amount is below typical BNPL minimums."]
    });
    return plans;
  }

  // ============ EDUCATION (Critical - this is where Flexaura wins) ============
  if (vertical === "education") {
    const thresholds = THRESHOLDS.education;
    const termCount: 2 | 3 = context.termCount ?? 3;
    const level = context.level ?? "primary";
    const enrolled = !!context.enrolled;
    const perTerm = Math.round(amountAED / termCount);

    if (amountAED <= thresholds.tier1Max) {
      // Tier 1: Pure BNPL zone (Tabby, Tamara)
      plans.push({
        id: "edu-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Split into 4 payments, 0% interest", "Fast approval for tuition fees"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "edu-bnpl-tamara",
        label: "Pay with Tamara",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tamara',
        notes: ["Sharia-compliant instalments", "Quick checkout"],
        redirectUrl: BNPL_PROVIDERS[1].redirect
      });
    } else if (amountAED <= thresholds.tier2Max) {
      // Tier 2: Tabby + EMI side-by-side (this is the key differentiator)
      plans.push({
        id: "edu-bnpl-tabby",
        label: "BNPL with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Quick approval, pay over 4 months", "Best for immediate enrollment"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "edu-emi",
        label: "Bank EMI",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: 12,
        provider: 'emi',
        isPrimary: true, // Both primary in this range
        notes: ["Pay over 12-24 months via bank", "Lower monthly payments"],
        redirectUrl: BANK_LENDERS[0].redirect
      });
    } else {
      // Tier 3 (> 25,000): EMI/Education Finance first, BNPL as partial-pay
      plans.push({
        id: "edu-finance-full",
        label: "Education Finance",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: 24,
        provider: 'emi',
        isPrimary: true,
        notes: [
          "Pay over 12-36 months with extended terms",
          "Single approval for the academic year",
          `Monthly payment: ~AED ${Math.round(amountAED / 24).toLocaleString()}`
        ],
        redirectUrl: BANK_LENDERS[1].redirect
      });
      // BNPL as partial-pay option for first term
      const bnplPortion = Math.min(perTerm, thresholds.tier1Max);
      const financePortion = amountAED - bnplPortion;
      plans.push({
        id: "edu-mix",
        label: "BNPL for first term + Finance the rest",
        breakdown: { bnplAED: bnplPortion, bankFinanceAED: financePortion },
        tenureMonths: 12,
        provider: 'tabby',
        notes: [
          `Pay first term (~AED ${bnplPortion.toLocaleString()}) via BNPL now`,
          `Finance remaining ~AED ${financePortion.toLocaleString()} over 12 months`,
          enrolled ? "Enrollment confirmed" : "Enrollment to be confirmed"
        ],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
    }

    return plans;
  }

  // ============ HEALTHCARE ============
  if (vertical === "healthcare") {
    const thresholds = THRESHOLDS.healthcare;
    const urgency = context.urgency ?? "Elective";

    if (amountAED <= thresholds.tier1Max) {
      // Tier 1: Tabby, Postpay
      plans.push({
        id: "hc-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: urgency === "Urgent" ? 3 : 6,
        provider: 'tabby',
        isPrimary: true,
        notes: [
          "Fast approval for medical bills",
          urgency === "Urgent" ? "Shorter tenor for urgent procedures" : "Split into monthly payments"
        ],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "hc-bnpl-postpay",
        label: "Pay with Postpay",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 3,
        provider: 'postpay',
        notes: ["Pay in 3 instalments", "Quick checkout"],
        redirectUrl: BNPL_PROVIDERS[2].redirect
      });
    } else if (amountAED <= thresholds.tier2Max) {
      // Tier 2: Tabby, Tamara
      plans.push({
        id: "hc-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 6,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Split into monthly payments", "Best for medical procedures"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "hc-bnpl-tamara",
        label: "Pay with Tamara",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 6,
        provider: 'tamara',
        notes: ["Sharia-compliant instalments", "Available for larger bills"],
        redirectUrl: BNPL_PROVIDERS[1].redirect
      });
    } else {
      // Tier 3 (> 15,000): Tabby + Bank EMI
      plans.push({
        id: "hc-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: Math.min(amountAED, 15000) },
        tenureMonths: 6,
        provider: 'tabby',
        isPrimary: true,
        notes: [`Split up to AED ${Math.min(amountAED, 15000).toLocaleString()}`, "Fastest approval"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "hc-emi",
        label: "Bank EMI",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: urgency === "Urgent" ? 12 : 24,
        provider: 'emi',
        notes: [
          "Extended payment terms for larger bills",
          `Monthly payment: ~AED ${Math.round(amountAED / (urgency === "Urgent" ? 12 : 24)).toLocaleString()}`,
          "Pre-approval available"
        ],
        redirectUrl: BANK_LENDERS[0].redirect
      });
    }

    return plans;
  }

  // ============ TRAVEL ============
  if (vertical === "travel") {
    const thresholds = THRESHOLDS.travel;

    if (amountAED <= thresholds.tier1Max) {
      // Tier 1 (≤ 8,000): Tabby, Tamara, Postpay
      plans.push({
        id: "travel-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Split into 4 payments, 0% interest", "Perfect for vacation packages"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "travel-bnpl-tamara",
        label: "Pay with Tamara",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tamara',
        notes: ["Sharia-compliant instalments"],
        redirectUrl: BNPL_PROVIDERS[1].redirect
      });
      plans.push({
        id: "travel-bnpl-postpay",
        label: "Pay with Postpay",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 3,
        provider: 'postpay',
        notes: ["Pay in 3 instalments"],
        redirectUrl: BNPL_PROVIDERS[2].redirect
      });
    } else if (amountAED <= thresholds.tier2Max) {
      // Tier 2 (8,000-15,000): Tabby primary, Tamara secondary, hide Postpay
      plans.push({
        id: "travel-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Best option for this amount", "Split into 4 payments"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "travel-bnpl-tamara",
        label: "Pay with Tamara",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tamara',
        notes: ["Sharia-compliant alternative", "Available for GCC residents"],
        redirectUrl: BNPL_PROVIDERS[1].redirect
      });
    } else {
      // Tier 3 (> 15,000): Tabby only + EMI fallback
      plans.push({
        id: "travel-bnpl-tabby",
        label: "Pay with Tabby",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 4,
        provider: 'tabby',
        isPrimary: true,
        notes: ["Best option for luxury travel", "Split into monthly payments"],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
      plans.push({
        id: "travel-emi",
        label: "Bank EMI",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: 12,
        provider: 'emi',
        notes: [
          "Extended payment terms up to 24 months",
          `Monthly payment: ~AED ${Math.round(amountAED / 12).toLocaleString()}`,
          "Ideal for premium travel experiences"
        ],
        redirectUrl: BANK_LENDERS[0].redirect
      });
    }

    return plans;
  }

  // Generic fallback
  return [{
    id: "generic-bnpl",
    label: "BNPL with Tabby",
    breakdown: { bnplAED: amountAED },
    tenureMonths: 4,
    provider: 'tabby',
    notes: ["Split into monthly payments"],
    redirectUrl: BNPL_PROVIDERS[0].redirect
  }];
}